import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

/**
 * The chip's SHAPE, without its interactive colour — exported so the non-navigable twins wear exactly
 * the same pill: an MRS whose id we could not resolve, and the "+N" overflow chip. Three hand-copied
 * class strings would drift the moment one of them is tuned.
 */
export const MRS_CHIP_SHAPE =
  'inline-flex h-[19px] items-center whitespace-nowrap rounded-md border border-border px-[7px] font-mono text-[10.5px] font-medium';

interface MrsLinkProps {
  mrsId: number;
  mrsNumber: string;
  className?: string;
  /**
   * `link` (default) — underlined text with a trailing external-link icon. Every existing caller.
   * `chip` — the prototype's bordered chip (h19, radius 6, 10.5px mono, `--info`), no icon.
   *
   * The design draws a plain <span> there, but dropping the anchor would remove real navigation to the
   * MRS. This keeps the link and wears the chip.
   */
  variant?: 'link' | 'chip';
  /**
   * PT-7 B1 — extra props for the anchor, spread AFTER its own (so `title: undefined` drops the chip's native
   * title). `MrsChip` passes its hover and focus handlers here. `onClick` stays this component's own: it stops
   * propagation so a click never also toggles the row the chip sits in.
   */
  linkProps?: Omit<React.ComponentPropsWithoutRef<'a'>, 'href' | 'target' | 'rel' | 'onClick'>;
}

/**
 * Renders an MRS number as a clickable link to the MRS detail page,
 * opening in a new tab. Includes an ExternalLink icon for visual clarity.
 */
export function MrsLink({ mrsId, mrsNumber, className, variant = 'link', linkProps }: MrsLinkProps) {
  const chip = variant === 'chip';
  return (
    <Link
      to={`/mro/issuance/mrs/${mrsId}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      // The `link` variant carries the trailing ExternalLink icon as its "opens in a new tab" signal;
      // the chip has no icon, so it says so in a native title instead. Native, not a Tooltip: this
      // renders once per ROW in the items table, and a Radix tooltip root per row is what turned one
      // picklist-cart tick into a 239ms long task.
      title={chip ? `${mrsNumber} — opens in a new tab` : undefined}
      className={cn(
        'inline-flex items-center transition-colors',
        chip
          ? cn(MRS_CHIP_SHAPE, 'text-info hover:bg-info/10')
          : 'gap-1 text-foreground underline decoration-muted-foreground/50 underline-offset-2 hover:decoration-foreground',
        className,
      )}
      {...linkProps}
    >
      {mrsNumber}
      {!chip && <ExternalLink className="h-3 w-3 flex-shrink-0" />}
    </Link>
  );
}
